import { isoDate as melDate, todayISO as melToday } from "@/lib/intl";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getCached, setCached } from "@/lib/pageStateCache";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Clock,
  Inbox,
  MessageSquare,
  RefreshCw,
  Search,
  TimerOff,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import SmsQuickSendDialog from "@/components/SmsQuickSendDialog";

const LABEL_NAMES = {
  CHECKED_IN: "Checked In",
  DNA: "DNA",
  LCXL: "LCXL",
};

function todayIso() {
  return melToday();
}

function shiftIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return melDate(d);
}

function timeOnly(dt) {
  if (!dt || dt.length < 16) return "";
  return dt.slice(11, 16);
}

function findLabelByName(labels, name) {
  return labels.find(
    (l) => (l?.name || "").trim().toLowerCase() === name.toLowerCase()
  );
}

function currentLabelKind(labels, mapping) {
  if (!Array.isArray(labels) || !labels.length) return null;
  const ids = new Set(labels.map((l) => Number(l.id)));
  if (mapping.checked_in_id && ids.has(mapping.checked_in_id)) return "checked_in";
  if (mapping.dna_id && ids.has(mapping.dna_id)) return "dna";
  if (mapping.lcxl_id && ids.has(mapping.lcxl_id)) return "lcxl";
  return null;
}

function StatusPill({ kind }) {
  if (!kind) {
    return (
      <span className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-zinc-300 bg-zinc-50 text-zinc-600">
        <Clock className="h-3 w-3" /> Pending
      </span>
    );
  }
  const map = {
    checked_in: { label: "Checked In", cls: "border-emerald-300 bg-emerald-50 text-emerald-800", icon: CheckCircle2 },
    dna: { label: "DNA", cls: "border-rose-300 bg-rose-50 text-rose-800", icon: CircleSlash },
    lcxl: { label: "LCXL", cls: "border-amber-300 bg-amber-50 text-amber-900", icon: TimerOff },
  };
  const m = map[kind];
  const Icon = m.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border",
        m.cls
      )}
    >
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export default function CheckIn() {
  const cached = getCached("checkin");
  const [date, setDate] = useState(cached?.date ?? todayIso());
  const [appointments, setAppointments] = useState(cached?.appointments ?? []);
  const [calendars, setCalendars] = useState(cached?.calendars ?? []);
  const [labels, setLabels] = useState(cached?.labels ?? []);
  const [calendarFilter, setCalendarFilter] = useState(cached?.calendarFilter ?? "all");
  const [search, setSearch] = useState(cached?.search ?? "");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(Boolean(cached?.appointments?.length));
  const [error, setError] = useState(null);
  // appointment ids currently being mutated, for UI lock
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [smsOpen, setSmsOpen] = useState(false);
  // Track the date value we last saw so the "reset on date change" effect
  // doesn't fire on the initial mount when we're just re-hydrating from the
  // page cache.
  const prevDateRef = useRef(date);

  useEffect(() => {
    setCached("checkin", {
      date, appointments, calendars, labels, calendarFilter, search, ts: Date.now(),
    });
  }, [date, appointments, calendars, labels, calendarFilter, search]);

  const labelMapping = useMemo(() => {
    const ci = findLabelByName(labels, LABEL_NAMES.CHECKED_IN);
    const dna = findLabelByName(labels, LABEL_NAMES.DNA);
    const lcxl = findLabelByName(labels, LABEL_NAMES.LCXL);
    return {
      checked_in_id: ci?.id ? Number(ci.id) : null,
      dna_id: dna?.id ? Number(dna.id) : null,
      lcxl_id: lcxl?.id ? Number(lcxl.id) : null,
      ready: !!(ci && dna && lcxl),
    };
  }, [labels]);

  const mutuallyExclusiveIds = useMemo(
    () =>
      [labelMapping.checked_in_id, labelMapping.dna_id, labelMapping.lcxl_id].filter(
        (x) => x != null
      ),
    [labelMapping]
  );

  const loadData = useCallback(async (iso) => {
    setLoading(true);
    setError(null);
    try {
      const [apptsRes, calsRes, labelsRes] = await Promise.all([
        api.get("/appointments", {
          // Check-in only needs raw appointments + labels + intake form
          // status. Skipping the calculated_price enrichment drops the
          // Acuity round-trip from ~30 s to ~1 s on busy accounts and
          // keeps the request comfortably under Cloudflare's 100 s cap.
          params: {
            minDate: iso, maxDate: iso, max: 1000, canceled: false,
            enrich_price: false,
          },
          timeout: 60000,
        }),
        api.get("/calendars"),
        api.get("/labels"),
      ]);
      // Acuity sometimes returns cancelled despite the flag — belt-and-braces.
      const rows = (apptsRes.data || []).filter((a) => !a.canceled);
      rows.sort((a, b) => (a.datetime || "").localeCompare(b.datetime || ""));
      setAppointments(rows);
      setCalendars(calsRes.data || []);
      setLabels(labelsRes.data || []);
      setHasFetched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load check-in list", { description: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when the date changes so the user has to click Apply again.
  // But don't fire on the initial mount — that would blow away appointments
  // we just re-hydrated from the page cache when navigating back to /schedule.
  useEffect(() => {
    if (prevDateRef.current === date) return;
    prevDateRef.current = date;
    setAppointments([]);
    setHasFetched(false);
    setError(null);
  }, [date]);

  async function setStatus(appt, kind) {
    if (!labelMapping.ready) {
      toast.error("Cannot update label", {
        description: 'Could not find "Checked In", "DNA", and "LCXL" labels in your Acuity account.',
      });
      return;
    }
    const targetId =
      kind === "checked_in"
        ? labelMapping.checked_in_id
        : kind === "dna"
        ? labelMapping.dna_id
        : kind === "lcxl"
        ? labelMapping.lcxl_id
        : null;

    setPendingIds((s) => {
      const next = new Set(s);
      next.add(appt.id);
      return next;
    });

    // Optimistic update
    const prevLabels = appt.labels || [];
    const optimisticLabels = (() => {
      const keep = prevLabels.filter(
        (l) => !mutuallyExclusiveIds.includes(Number(l.id))
      );
      if (targetId == null) return keep;
      const labelObj = labels.find((l) => Number(l.id) === targetId);
      return labelObj ? [...keep, labelObj] : keep;
    })();
    setAppointments((rows) =>
      rows.map((r) => (r.id === appt.id ? { ...r, labels: optimisticLabels } : r))
    );

    try {
      const r = await api.put(`/appointments/${appt.id}/labels`, {
        set_label_id: targetId,
        clear_label_ids: mutuallyExclusiveIds,
      });
      setAppointments((rows) =>
        rows.map((row) =>
          row.id === appt.id ? { ...row, labels: r.data?.labels || optimisticLabels } : row
        )
      );
      const labelText =
        kind === "checked_in"
          ? "Checked In"
          : kind === "dna"
          ? "DNA"
          : kind === "lcxl"
          ? "LCXL"
          : "Cleared";
      toast.success(`${labelText} · ${displayName(appt)}`);
    } catch (e) {
      // Rollback optimistic
      setAppointments((rows) =>
        rows.map((row) => (row.id === appt.id ? { ...row, labels: prevLabels } : row))
      );
      toast.error("Update failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setPendingIds((s) => {
        const next = new Set(s);
        next.delete(appt.id);
        return next;
      });
    }
  }

  async function clearStatus(appt) {
    setStatusKey(appt, null);
  }

  async function setStatusKey(appt, kind) {
    // wrapper allowing "clear" semantics (kind=null)
    if (!labelMapping.ready) return;
    setPendingIds((s) => {
      const next = new Set(s);
      next.add(appt.id);
      return next;
    });
    const prevLabels = appt.labels || [];
    const keep = prevLabels.filter(
      (l) => !mutuallyExclusiveIds.includes(Number(l.id))
    );
    setAppointments((rows) =>
      rows.map((r) => (r.id === appt.id ? { ...r, labels: keep } : r))
    );
    try {
      const r = await api.put(`/appointments/${appt.id}/labels`, {
        set_label_id: null,
        clear_label_ids: mutuallyExclusiveIds,
      });
      setAppointments((rows) =>
        rows.map((row) =>
          row.id === appt.id ? { ...row, labels: r.data?.labels || keep } : row
        )
      );
      toast.success(`Cleared · ${displayName(appt)}`);
    } catch (e) {
      setAppointments((rows) =>
        rows.map((row) => (row.id === appt.id ? { ...row, labels: prevLabels } : row))
      );
      toast.error("Update failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setPendingIds((s) => {
        const next = new Set(s);
        next.delete(appt.id);
        return next;
      });
    }
  }

  function displayName(a) {
    const f = (a.firstName || "").trim();
    const l = (a.lastName || "").trim();
    return `${f} ${l}`.trim() || a.email || "(walk-in)";
  }

  const filtered = useMemo(() => {
    let rows = appointments;
    if (calendarFilter !== "all") {
      rows = rows.filter((a) => String(a.calendarID) === calendarFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) => {
        return (
          (a.firstName || "").toLowerCase().includes(q) ||
          (a.lastName || "").toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q) ||
          (a.type || "").toLowerCase().includes(q)
        );
      });
    }
    return rows;
  }, [appointments, calendarFilter, search]);

  const counts = useMemo(() => {
    let ci = 0,
      dna = 0,
      lcxl = 0,
      pending = 0;
    for (const a of filtered) {
      const k = currentLabelKind(a.labels, labelMapping);
      if (k === "checked_in") ci++;
      else if (k === "dna") dna++;
      else if (k === "lcxl") lcxl++;
      else pending++;
    }
    return { ci, dna, lcxl, pending, total: filtered.length };
  }, [filtered, labelMapping]);

  return (
    <div className="space-y-6" data-testid="checkin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Bulk check-in
          </div>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Tap a row to mark the client as Checked In, DNA, or LCXL. Changes
            sync to Acuity immediately. Labels are mutually exclusive.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(shiftIso(date, -1))}
            data-testid="checkin-prev-day"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 w-[160px] font-mono-num"
            data-testid="checkin-date-input"
          />
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(shiftIso(date, 1))}
            data-testid="checkin-next-day"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(todayIso())}
            data-testid="checkin-today-btn"
          >
            Today
          </Button>
          <Button
            variant={hasFetched ? "outline" : "default"}
            size="sm"
            className={cn(
              hasFetched
                ? "border-zinc-300 hover:bg-zinc-100"
                : "bg-zinc-950 hover:bg-zinc-800 text-white",
            )}
            onClick={() => loadData(date)}
            disabled={loading}
            data-testid="checkin-refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin", !hasFetched && "mr-1.5")} />
            {!hasFetched && "Apply"}
          </Button>
        </div>
      </div>

      {!labelMapping.ready && labels.length > 0 && (
        <Card className="p-4 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
          Could not find one or more of <code>Checked In</code>, <code>DNA</code>,{" "}
          <code>LCXL</code> in your Acuity labels (Integrations → Labels). Please
          create them and refresh.
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="checkin-totals">
        <StatTile label="Total" value={counts.total} />
        <StatTile label="Checked In" value={counts.ci} tone="emerald" />
        <StatTile label="DNA" value={counts.dna} tone="rose" />
        <StatTile label="LCXL" value={counts.lcxl} tone="amber" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or class"
            className="h-9 w-[260px] pl-8"
            data-testid="checkin-search"
          />
        </div>
        <Select value={calendarFilter} onValueChange={setCalendarFilter}>
          <SelectTrigger className="h-9 w-[220px]" data-testid="checkin-calendar-filter">
            <SelectValue placeholder="All instructors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All instructors</SelectItem>
            {calendars.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-9 border-zinc-300 hover:bg-zinc-100 ml-auto"
          onClick={() => setSmsOpen(true)}
          disabled={filtered.length === 0}
          data-testid="checkin-sms-class-btn"
          title={filtered.length ? `SMS the ${filtered.length} client(s) in this view` : "No attendees to message"}
        >
          <MessageSquare className="h-4 w-4 mr-1.5" /> SMS class
        </Button>
      </div>

      {error && (
        <Card className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
          {error}
        </Card>
      )}

      {!hasFetched && !loading ? (
        <Card className="p-12 text-center border-2 border-dashed border-zinc-200 bg-zinc-50" data-testid="checkin-empty">
          <Clock className="h-8 w-8 text-zinc-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-700 font-medium">Pick a date, then Apply</p>
          <p className="mt-1 text-sm text-zinc-500">
            Check-in is pulled from Acuity on demand — click <span className="font-medium text-zinc-900">Apply</span> above to load.
          </p>
        </Card>
      ) : loading && !appointments.length ? (
        <Card className="p-12 text-center border border-zinc-200">
          <RefreshCw className="h-8 w-8 text-zinc-400 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-zinc-600">Loading appointments…</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border border-dashed border-zinc-200 bg-zinc-50">
          <Inbox className="h-8 w-8 text-zinc-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-600">
            No appointments for this date{calendarFilter !== "all" ? " / instructor" : ""}.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden border border-zinc-200" data-testid="checkin-list">
          <ul className="divide-y divide-zinc-100">
            {filtered.map((a) => {
              const cal = calendars.find((c) => c.id === a.calendarID);
              const kind = currentLabelKind(a.labels, labelMapping);
              const pending = pendingIds.has(a.id);
              return (
                <li
                  key={a.id}
                  data-testid={`checkin-row-${a.id}`}
                  className={cn(
                    "grid grid-cols-1 sm:grid-cols-[88px_1fr_auto] gap-3 px-4 py-3 items-center transition-colors",
                    kind === "checked_in" && "bg-emerald-50/40",
                    kind === "dna" && "bg-rose-50/40",
                    kind === "lcxl" && "bg-amber-50/40"
                  )}
                >
                  <div className="font-mono-num text-sm text-zinc-700">
                    {timeOnly(a.datetime)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-950">
                        {displayName(a)}
                      </span>
                      <StatusPill kind={kind} />
                    </div>
                    <div className="text-xs text-zinc-500 truncate">
                      {a.type}
                      {cal ? ` · ${cal.name}` : ""}
                      {a.email ? ` · ${a.email}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ActionButton
                      label="Check In"
                      active={kind === "checked_in"}
                      tone="emerald"
                      disabled={pending || !labelMapping.ready}
                      onClick={() =>
                        kind === "checked_in"
                          ? setStatusKey(a, null)
                          : setStatus(a, "checked_in")
                      }
                      testId={`checkin-btn-ci-${a.id}`}
                    />
                    <ActionButton
                      label="DNA"
                      active={kind === "dna"}
                      tone="rose"
                      disabled={pending || !labelMapping.ready}
                      onClick={() =>
                        kind === "dna" ? setStatusKey(a, null) : setStatus(a, "dna")
                      }
                      testId={`checkin-btn-dna-${a.id}`}
                    />
                    <ActionButton
                      label="LCXL"
                      active={kind === "lcxl"}
                      tone="amber"
                      disabled={pending || !labelMapping.ready}
                      onClick={() =>
                        kind === "lcxl"
                          ? setStatusKey(a, null)
                          : setStatus(a, "lcxl")
                      }
                      testId={`checkin-btn-lcxl-${a.id}`}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <SmsQuickSendDialog
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        title={`SMS class (${filtered.length} attendees)`}
        source="schedule_class"
        recipients={filtered.map((a) => ({
          to: a.phone,
          raw_phone: a.phone,
          name: [a.firstName, a.lastName].filter(Boolean).join(" "),
          first_name: a.firstName,
          last_name: a.lastName,
          email: a.email,
          appointment_id: a.id,
          appointment_type: a.type,
          appointment_datetime: a.datetime,
          valid: !!a.phone,
          reason: a.phone ? null : "No phone on file",
        }))}
      />
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50",
    rose: "border-rose-200 bg-rose-50",
    amber: "border-amber-200 bg-amber-50",
  }[tone] || "border-zinc-200 bg-white";
  return (
    <div className={cn("px-3 py-2 rounded-md border", toneCls)}>
      <div className="text-2xs uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-0.5 font-mono-num text-xl tracking-tight text-zinc-950">{value}</div>
    </div>
  );
}

function ActionButton({ label, active, tone, disabled, onClick, testId }) {
  const toneActive = {
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
    rose: "bg-rose-600 hover:bg-rose-700 text-white border-rose-600",
    amber: "bg-amber-500 hover:bg-amber-600 text-white border-amber-500",
  }[tone];
  const toneIdle = {
    emerald: "border-zinc-300 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-800 text-zinc-700",
    rose: "border-zinc-300 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-800 text-zinc-700",
    amber: "border-zinc-300 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-800 text-zinc-700",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        "h-8 px-3 rounded-md border text-xs font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        active ? toneActive : toneIdle
      )}
    >
      {label}
    </button>
  );
}
